import React, { useState } from 'react';
import { CreditCardIcon, CheckCircleIcon, ChevronDownIcon, ExternalLinkIcon, DownloadIcon, AlertCircleIcon } from 'lucide-react';
export const PaymentsPayoutsSection = () => {
  // Placeholder data
  const [payoutData, setPayoutData] = useState({
    stripeConnected: true,
    stripeEmail: 'john@email.com',
    paypalConnected: false,
    paypalEmail: '',
    payoutFrequency: 'biweekly'
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    ...payoutData
  });
  // Mock transaction history data
  const transactions = [{
    id: 'tx-001',
    date: 'Aug 1, 2025',
    amount: 120.0,
    method: 'Stripe',
    status: 'Paid'
  }, {
    id: 'tx-002',
    date: 'Jul 15, 2025',
    amount: 180.0,
    method: 'PayPal',
    status: 'Paid'
  }, {
    id: 'tx-003',
    date: 'Jul 1, 2025',
    amount: 200.0,
    method: 'Stripe',
    status: 'Pending'
  }];
  // Mock earnings data
  const earnings = {
    total: 1620.0,
    thisMonth: 230.0,
    lastMonth: 310.0,
    nextPayout: 'Aug 15, 2025'
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPayoutData({
      ...formData
    });
    setIsEditing(false);
  };
  // Simulate connecting/disconnecting payment methods
  const toggleStripeConnection = () => {
    if (isEditing) {
      setFormData(prev => ({
        ...prev,
        stripeConnected: !prev.stripeConnected,
        stripeEmail: prev.stripeConnected ? '' : 'john@email.com'
      }));
    }
  };
  const togglePayPalConnection = () => {
    if (isEditing) {
      setFormData(prev => ({
        ...prev,
        paypalConnected: !prev.paypalConnected,
        paypalEmail: prev.paypalConnected ? '' : 'john@email.com'
      }));
    }
  };
  return <div data-id="element-3248">
      <div className="flex justify-between items-center mb-6" data-id="element-3249">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3250">
          Payments & Payouts
        </h2>
        <button onClick={() => setIsEditing(!isEditing)} className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-[#1A3330] text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors" data-id="element-3251">
          {isEditing ? 'Cancel' : 'Edit Settings'}
        </button>
      </div>
      <form onSubmit={handleSubmit} data-id="element-3252">
        <div className="space-y-8 mb-8" data-id="element-3253">
          {/* Payout Methods */}
          <div data-id="element-3254">
            <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-3255">
              Payout Methods
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3256">
              {/* Stripe */}
              <div className="bg-white dark:bg-[#1A3330] rounded-lg border border-gray-200 dark:border-[#162825] p-4" data-id="element-3257">
                <div className="flex items-start justify-between mb-4" data-id="element-3258">
                  <div className="flex items-center" data-id="element-3259">
                    <div className="w-10 h-10 bg-[#635BFF] rounded-md flex items-center justify-center mr-3 flex-shrink-0" data-id="element-3260">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white" data-id="element-3261">
                        <path d="M2 10C2 7.79086 3.79086 6 6 6H18C20.2091 6 22 7.79086 22 10V14C22 16.2091 20.2091 18 18 18H6C3.79086 18 2 16.2091 2 14V10Z" stroke="white" strokeWidth="2" data-id="element-3262" />
                        <path d="M6 12H8" stroke="white" strokeWidth="2" strokeLinecap="round" data-id="element-3263" />
                        <path d="M12 12H14" stroke="white" strokeWidth="2" strokeLinecap="round" data-id="element-3264" />
                        <path d="M18 12H18.01" stroke="white" strokeWidth="2" strokeLinecap="round" data-id="element-3265" />
                      </svg>
                    </div>
                    <div data-id="element-3266">
                      <h4 className="font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3267">
                        Stripe
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-3268">
                        Credit card payouts
                      </p>
                    </div>
                  </div>
                  {formData.stripeConnected && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400" data-id="element-3269">
                      <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-3270" />
                      Connected
                    </span>}
                </div>
                <div className="mb-4" data-id="element-3271">
                  <p className="text-sm text-gray-700 dark:text-[#B0C1BD]" data-id="element-3272">
                    Status:{' '}
                    <span className={formData.stripeConnected ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-[#7A8F8A]'} data-id="element-3273">
                      {formData.stripeConnected ? `Connected as ${formData.stripeEmail}` : 'Not connected'}
                    </span>
                  </p>
                </div>
                <button type="button" onClick={toggleStripeConnection} className={`w-full py-2 px-4 rounded-md text-sm font-medium ${formData.stripeConnected ? 'bg-white dark:bg-[#162825] border border-gray-300 dark:border-[#1A3330] text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330]' : 'bg-[#635BFF] text-white hover:bg-[#4B44C9]'} transition-colors ${!isEditing ? 'opacity-60 cursor-not-allowed' : ''}`} disabled={!isEditing} data-id="element-3274">
                  <div className="flex items-center justify-center" data-id="element-3275">
                    {formData.stripeConnected ? 'Disconnect Stripe' : <>
                        Connect Stripe
                        <ExternalLinkIcon className="w-3.5 h-3.5 ml-1.5" data-id="element-3276" />
                      </>}
                  </div>
                </button>
              </div>
              {/* PayPal */}
              <div className="bg-white dark:bg-[#1A3330] rounded-lg border border-gray-200 dark:border-[#162825] p-4" data-id="element-3277">
                <div className="flex items-start justify-between mb-4" data-id="element-3278">
                  <div className="flex items-center" data-id="element-3279">
                    <div className="w-10 h-10 bg-[#0070BA] rounded-md flex items-center justify-center mr-3 flex-shrink-0" data-id="element-3280">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white" data-id="element-3281">
                        <path d="M17.5 7.2C19.2 8.2 20 10 20 12C20 14.5 17.5 16.5 15 16.5H12.5L11.4 22H8L10 10H15C16 10 17 9.4 17.5 7.2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3282" />
                        <path d="M13.5 4C15.2 5 16 6.8 16 8.8C16 11.3 13.5 13.3 11 13.3H8.5L7.4 18.8H4L6 6.8H11C12 6.8 13 6.2 13.5 4Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3283" />
                      </svg>
                    </div>
                    <div data-id="element-3284">
                      <h4 className="font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3285">
                        PayPal
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-3286">
                        Direct bank payouts
                      </p>
                    </div>
                  </div>
                  {formData.paypalConnected && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400" data-id="element-3287">
                      <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-3288" />
                      Connected
                    </span>}
                </div>
                <div className="mb-4" data-id="element-3289">
                  <p className="text-sm text-gray-700 dark:text-[#B0C1BD]" data-id="element-3290">
                    Status:{' '}
                    <span className={formData.paypalConnected ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-[#7A8F8A]'} data-id="element-3291">
                      {formData.paypalConnected ? `Connected as ${formData.paypalEmail}` : 'Not connected'}
                    </span>
                  </p>
                </div>
                <button type="button" onClick={togglePayPalConnection} className={`w-full py-2 px-4 rounded-md text-sm font-medium ${formData.paypalConnected ? 'bg-white dark:bg-[#162825] border border-gray-300 dark:border-[#1A3330] text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330]' : 'bg-[#0070BA] text-white hover:bg-[#005A99]'} transition-colors ${!isEditing ? 'opacity-60 cursor-not-allowed' : ''}`} disabled={!isEditing} data-id="element-3292">
                  <div className="flex items-center justify-center" data-id="element-3293">
                    {formData.paypalConnected ? 'Disconnect PayPal' : <>
                        Connect PayPal
                        <ExternalLinkIcon className="w-3.5 h-3.5 ml-1.5" data-id="element-3294" />
                      </>}
                  </div>
                </button>
              </div>
            </div>
          </div>
          {/* Payout Frequency */}
          <div data-id="element-3295">
            <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-3296">
              Payout Frequency
            </h3>
            <div className="max-w-md" data-id="element-3297">
              <div className="flex flex-col" data-id="element-3298">
                <label htmlFor="payoutFrequency" className="block text-sm font-medium text-gray-700 dark:text-[#B0C1BD] mb-1" data-id="element-3299">
                  How often would you like to receive payouts?
                </label>
                <div className="relative" data-id="element-3300">
                  <select id="payoutFrequency" name="payoutFrequency" value={formData.payoutFrequency} onChange={handleInputChange} disabled={!isEditing} className={`w-full px-3 py-2 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#162825] text-gray-900 dark:text-[#F8FAF9] appearance-none ${!isEditing ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-3301">
                    <option value="weekly" data-id="element-3302">Weekly</option>
                    <option value="biweekly" data-id="element-3303">Biweekly</option>
                    <option value="monthly" data-id="element-3304">Monthly</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-3305">
                    <ChevronDownIcon className="w-4 h-4 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3306" />
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3307">
                  Payouts will be sent automatically based on your selected
                  schedule.
                </p>
              </div>
            </div>
          </div>
          {/* Earnings Summary */}
          <div data-id="element-3308">
            <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-3309">
              Earnings Summary
            </h3>
            <div className="bg-white dark:bg-[#1A3330] rounded-lg border border-gray-200 dark:border-[#162825] p-5" data-id="element-3310">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3311">
                <div className="p-3" data-id="element-3312">
                  <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mb-1" data-id="element-3313">
                    Total Earnings
                  </p>
                  <p className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3314">
                    ${earnings.total.toFixed(2)}
                  </p>
                </div>
                <div className="p-3" data-id="element-3315">
                  <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mb-1" data-id="element-3316">
                    This Month
                  </p>
                  <p className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3317">
                    ${earnings.thisMonth.toFixed(2)}
                  </p>
                </div>
                <div className="p-3" data-id="element-3318">
                  <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mb-1" data-id="element-3319">
                    Last Month
                  </p>
                  <p className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3320">
                    ${earnings.lastMonth.toFixed(2)}
                  </p>
                </div>
                <div className="p-3" data-id="element-3321">
                  <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mb-1" data-id="element-3322">
                    Next Payout
                  </p>
                  <p className="text-xl font-semibold text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-3323">
                    {earnings.nextPayout}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Transaction History */}
          <div data-id="element-3324">
            <div className="flex justify-between items-center mb-4" data-id="element-3325">
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3326">
                Transaction History
              </h3>
              <button type="button" className="inline-flex items-center text-sm font-medium text-[#2DB489] dark:text-[#4ED1A0] hover:text-[#28A07A] dark:hover:text-[#3BC090]" data-id="element-3327">
                <DownloadIcon className="w-4 h-4 mr-1" data-id="element-3328" />
                Export CSV
              </button>
            </div>
            <div className="bg-white dark:bg-[#1A3330] rounded-lg border border-gray-200 dark:border-[#162825] overflow-hidden" data-id="element-3329">
              <div className="overflow-x-auto" data-id="element-3330">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-[#162825]" data-id="element-3331">
                  <thead className="bg-gray-50 dark:bg-[#162825]" data-id="element-3332">
                    <tr data-id="element-3333">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#7A8F8A] uppercase tracking-wider" data-id="element-3334">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#7A8F8A] uppercase tracking-wider" data-id="element-3335">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#7A8F8A] uppercase tracking-wider" data-id="element-3336">
                        Method
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#7A8F8A] uppercase tracking-wider" data-id="element-3337">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-[#1A3330] divide-y divide-gray-200 dark:divide-[#162825]" data-id="element-3338">
                    {transactions.map(transaction => <tr key={transaction.id} data-id="element-3339">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-[#B0C1BD]" data-id="element-3340">
                          {transaction.date}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-[#F8FAF9]" data-id="element-3341">
                          ${transaction.amount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-[#B0C1BD]" data-id="element-3342">
                          {transaction.method}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-3343">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${transaction.status === 'Paid' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'}`} data-id="element-3344">
                            {transaction.status}
                          </span>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-3 bg-gray-50 dark:bg-[#162825] text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-3345">
                This is a preview of your past and upcoming payouts.
              </div>
            </div>
          </div>
          {/* Tax Info */}
          <div className="bg-gray-50 dark:bg-[#162825] rounded-lg p-4 border border-gray-200 dark:border-[#1A3330]" data-id="element-3346">
            <div className="flex items-start" data-id="element-3347">
              <AlertCircleIcon className="w-5 h-5 text-gray-400 dark:text-[#7A8F8A] mr-3 mt-0.5 flex-shrink-0" data-id="element-3348" />
              <div data-id="element-3349">
                <h4 className="text-sm font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-3350">
                  Tax Information
                </h4>
                <p className="text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-3351">
                  We may require tax documents in the future to comply with
                  local laws (e.g., W-9 or W-8BEN). You'll be notified if action
                  is needed.
                </p>
              </div>
            </div>
          </div>
        </div>
        {isEditing && <div className="flex justify-end" data-id="element-3352">
            <button type="submit" className="px-5 py-2.5 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white font-medium rounded-lg hover:shadow-md transition-all duration-200" data-id="element-3353">
              Save Changes
            </button>
          </div>}
      </form>
    </div>;
};